import React, { useEffect, useMemo, useState } from 'react';
import { Paper } from '@mui/material';
import { useTable, usePagination } from 'react-table';
import { useLocation } from 'react-router-dom';
import ApiBaseURL from '../ApiBaseUrl/ApiBaseURL';

const PaginatedTripTable = React.memo(({ data, title, columns }) => {
    const totalRow = useMemo(() => data.find((d) => d.vendor === 'Total Trip'), [data]);
    const pagedData = useMemo(() => data.filter((d) => d.vendor !== 'Total Trip'), [data]);

    const tableInstance = useTable(
        {
            columns,
            data: pagedData,
            initialState: { pageIndex: 0, pageSize: 10 },
        },
        usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        pageOptions,
        state: { pageIndex },
    } = tableInstance;

    return (
        <Paper className="p-3 mb-4" elevation={3}>
            <h5 className="fw-bold mb-3">{title}</h5>

            <table {...getTableProps()} className="table table-bordered">
                {/* <thead className="table-dark" style={{ textAlign: 'center' }}> */}
                <thead className="table-dark">
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps()} key={column.id}>
                                    {column.render('Header')}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                {/* <tbody {...getTableBodyProps()} style={{ textAlign: 'end' }}> */}
                <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()} key={row.id}>
                                {row.cells.map((cell) => (
                                    <td {...cell.getCellProps()} key={cell.column.id}>
                                        {cell.render('Cell')}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}

                    {/* Always show total trip row */}
                    {totalRow && (
                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f4f4f4' }}>
                            <td>{totalRow.vendor}</td>
                            <td>{totalRow.date}</td>
                            <td>{totalRow.A}</td>
                            <td>{totalRow.B}</td>
                            <td>{totalRow.C}</td>
                            <td>{totalRow.total}</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Pagination Controls */}
            <div className="pagination d-flex justify-content-between align-items-center px-3 py-2">
                <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    className="btn btn-sm btn-outline-secondary"
                >
                    Previous
                </button>
                <span>
                    Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
                </span>
                <button
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    className="btn btn-sm btn-outline-secondary"
                >
                    Next
                </button>
            </div>
        </Paper>
    );
});

const ReportTabularViewShiftWise = (props) => {
    const [softTripData, setSoftTripData] = useState([]);
    const [hardTripData, setHardTripData] = useState([]);
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    const {
        vehicleTypeId,
        vendorId,
        driverId,
        vehicleId,
        period,
    } = props.vehicleTypeId ? props : location.state || {};

    const fetchConsumptionData = async () => {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');
        if (!userId || !token) return;

        setLoading(true);
        try {
            const response = await fetch(`${ApiBaseURL}/dashboard/v1/get/trips-by-shift-vendor`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    userId: userId,
                },
                body: JSON.stringify({
                    vehicleTypeId,
                    vendorId,
                    driverId,
                    vehicleId,
                    period,
                }),
            });

            const result = await response.json();

            if (response.ok && result.statusDescription.statusCode === 200) {
                const softData = [];
                const hardData = [];

                result.data.forEach((entry) => {
                    const { tripDate, vendors } = entry;

                    vendors.forEach((vendor) => {
                        const softRow = { vendor: vendor.vendorName, date: tripDate, A: 0, B: 0, C: 0, total: 0 };
                        const hardRow = { vendor: vendor.vendorName, date: tripDate, A: 0, B: 0, C: 0, total: 0 };

                        vendor.shifts.forEach((shift) => {
                            softRow[shift.shift] = shift.softTripCount || 0;
                            hardRow[shift.shift] = shift.hardTripCount || 0;
                        });

                        softRow.total = softRow.A + softRow.B + softRow.C;
                        hardRow.total = hardRow.A + hardRow.B + hardRow.C;

                        softData.push(softRow);
                        hardData.push(hardRow);
                    });
                });

                const calcTotalRow = (data) =>
                    data.reduce(
                        (acc, curr) => {
                            acc.A += curr.A;
                            acc.B += curr.B;
                            acc.C += curr.C;
                            acc.total += curr.total;
                            return acc;
                        },
                        { vendor: 'Total Trip', date: '', A: 0, B: 0, C: 0, total: 0 }
                    );

                setSoftTripData([...softData, calcTotalRow(softData)]);
                setHardTripData([...hardData, calcTotalRow(hardData)]);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConsumptionData();
    }, [vehicleTypeId, vendorId, driverId, vehicleId, period]);

    const columns = useMemo(
        () => [
            { Header: 'Vendors', accessor: 'vendor' },
            { Header: 'Date', accessor: 'date' },
            { Header: 'Shift_A', accessor: 'A' },
            { Header: 'Shift_B', accessor: 'B' },
            { Header: 'Shift_C', accessor: 'C' },
            { Header: 'Total', accessor: 'total' },
        ],
        []
    );

    return (
        <div className="p-3">
            {loading ? (
                <div className="text-center py-5">Loading...</div>
            ) : (
                <>
                    <PaginatedTripTable data={softTripData} title="Composite Production Report" columns={columns} />
                    <PaginatedTripTable data={hardTripData} title="Re-Handling Production Report" columns={columns} />
                </>
            )}
        </div>
    );
};

export default ReportTabularViewShiftWise;